import { useState, useEffect } from "react";
import { affiliateAPI } from "../services/api";
import { Card, Button } from "./index";
import { Copy, Share2, Users, TrendingUp, Check, Gift } from "lucide-react";

/**
 * ReferralCard - Shows user's personal invite link and referral stats
 * Displayed on the Account page
 */
export const ReferralCard = ({ className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        affiliateAPI.getCode(),
        affiliateAPI.getStats()
      ]);
      setData({
        ...codeRes.data,
        ...statsRes.data
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!data?.link) return;
    
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = data.link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (!data?.share_text) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Reclaim",
          text: data.share_text,
          url: data.link
        });
      } catch (e) {
        if (e.name !== "AbortError") {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return (
      <Card className={`p-5 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-[#E6E2DD] rounded w-32" />
          <div className="h-10 bg-[#E6E2DD] rounded" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={`overflow-hidden ${className}`} data-testid="referral-card">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-[#4A6741]/10 to-[#4A6741]/5 border-b border-[#E6E2DD]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#4A6741] rounded-full flex items-center justify-center">
            <Gift className="text-white" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#1F2937]">Invite Friends</h3>
            <p className="text-sm text-[#6B7280]">Share the gift of presence</p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Referral Code */}
        <div>
          <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">Your invite code</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 px-4 py-3 bg-[#F0EFEB] rounded-xl font-mono text-lg text-[#1F2937] tracking-wider">
              {data?.code || "---"}
            </div>
            <button
              onClick={handleCopy}
              className={`p-3 rounded-xl transition-all ${
                copied 
                  ? 'bg-[#4A6741] text-white' 
                  : 'bg-[#F0EFEB] text-[#6B7280] hover:bg-[#E6E2DD]'
              }`}
              data-testid="copy-code-btn"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
          </div>
        </div>

        {/* Share Link */}
        <div>
          <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">Your invite link</p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={data?.link || ""}
              className="flex-1 px-4 py-3 bg-[#F0EFEB] rounded-xl text-sm text-[#6B7280] truncate"
              data-testid="referral-link-input"
            />
            <Button
              onClick={handleShare}
              className="px-4"
              data-testid="share-link-btn"
            >
              <Share2 size={18} />
              Share
            </Button>
          </div>
        </div>

        {/* Stats */}
        {data?.total_referrals > 0 && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#E6E2DD]">
            <div className="p-3 bg-[#F9FAFB] rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Users size={14} className="text-[#4A6741]" />
                <span className="text-xs text-[#6B7280]">Invited</span>
              </div>
              <p className="text-xl font-heading text-[#1F2937]">{data?.total_referrals || 0}</p>
            </div>
            <div className="p-3 bg-[#F9FAFB] rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={14} className="text-[#4A6741]" />
                <span className="text-xs text-[#6B7280]">This Month</span>
              </div>
              <p className="text-xl font-heading text-[#1F2937]">{data?.this_month || 0}</p>
            </div>
          </div>
        )}

        {/* Recent referrals */}
        {data?.recent_referrals?.length > 0 && (
          <div className="pt-3 border-t border-[#E6E2DD]">
            <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-3">Recent invites accepted</p>
            <div className="space-y-2">
              {data.recent_referrals.slice(0, 3).map((ref, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-[#F9FAFB] rounded-lg">
                  <div className="w-8 h-8 bg-[#4A6741]/10 rounded-full flex items-center justify-center">
                    <span className="text-sm text-[#4A6741]">{ref.name?.[0] || "?"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1F2937] truncate">{ref.name}</p>
                    <p className="text-xs text-[#6B7280]">
                      {new Date(ref.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Encouragement for new users */}
        {(!data?.total_referrals || data.total_referrals === 0) && (
          <div className="p-4 bg-gradient-to-r from-[#D4A373]/10 to-[#D4A373]/5 rounded-xl">
            <p className="text-sm text-[#6B7280] text-center">
              Share your link and help friends discover their path to presence 🌿
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ReferralCard;
